import MathsUtils from '@/utils/mathsUtils'


/**
 * DXF References
 * 
 * https://ezdxf.mozman.at/docs/dxfinternals/filestructure.html#minimal-dxf-content
 */


/**
 * DXF Writer Class
 */
export class DXFWriter {
  constructor() {
    this.dxfCodeList = []
    this.currentLayer = '0'
    this.currentSection = null
  }


  /**
   * Write a standard header section with defaults & provided info
   */
  writeStandardHeader(options = {}) {
    /*
    JB: 5/02/2023 - Disabled
    Pointless. If we need metric to show in DoubleCad, need to use an updated DXF version which requires lots of headers, blocks, tables, classes, etc
    
    this.writeCode(0, 'SECTION')
    this.writeCode(2, 'HEADER')

    // Versioning
    this.writeHeaderVariable('$ACADVER', 1, 'AC1009')   // DXF R12
    this.writeHeaderVariable('$ACADMAINTVER', 70, '104')
    this.writeHeaderVariable('$DWGCODEPAGE', 3, 'ANSI_936')
    this.writeHeaderVariable('$INSUNITS', 70, '4')   // Millimetres
    this.writeHeaderVariable('$MEASUREMENT', 70, '1')   // Metric
    this.writeHeaderVariable('$LUNITS', 70, '2')   // Metric
    
    // Origin
    this.writeCode(9, '$INSBASE')
    this.writeDoubleValueCode(10, 0)
    this.writeDoubleValueCode(20, 0)
    this.writeDoubleValueCode(30, 0)

    // Extents
    if(options.extents) {
      this.writeCode(9, '$EXTMIN')
      this.writeDoubleValueCode(10, options.extents.min.x)
      this.writeDoubleValueCode(20, options.extents.min.y)
      this.writeDoubleValueCode(30, 0)
      this.writeCode(9, '$EXTMAX')
      this.writeDoubleValueCode(10, options.extents.max.x)
      this.writeDoubleValueCode(20, options.extents.max.y)
      this.writeDoubleValueCode(30, 0)
    }

    // Angles
    this.writeHeaderVariable('$ANGBASE', 50, 0)     // Angle base for 0 degrees
    this.writeHeaderVariable('$ANGDIR', 70, 0)      // Counterclockwise

    this.writeCode(0, 'ENDSEC')
    */
  }


  /**
   * Start a new section
   */
  startSection(type) {
    // Close existing section
    if(this.currentSection) {
      this.writeCode(0, 'ENDSEC')
    }
    this.currentSection = type
    this.writeCode(0, 'SECTION')
    this.writeCode(2, type)
  }


  /**
   * Set the current layer
   */
  setCurrentLayer(layer) {
    this.currentLayer = layer
  }
 
  /**
   * Write a DXF code & data pair to the list
   * Requires 2 spaces by certain tooling/software (eg, Jetstream software)
   */
  writeCode(code, data) {
    this.dxfCodeList.push('  ' + code)
    this.dxfCodeList.push(data)
  }


  /**
   * Write a DXF attribute code & data pair to the list
   * Requires only 1 spaces by certain tooling/software (eg, Jetstream software)
   */
  writeAttributeCode(code, data) {
    this.dxfCodeList.push(' ' + code)
    this.dxfCodeList.push(data)
  }


  /**
   * Write a header variable
   */
  writeHeaderVariable(variableName, variableCode, variableValue) {
    this.writeCode(9, variableName)
    this.writeCode(variableCode, variableValue)
  }


  /**
   * Write a DXF code & point value pair to the list
   */
  writePointValueCode(code, value) {
    let decimalPlaces = 8
    this.writeAttributeCode(code, MathsUtils.roundNumber(parseFloat(value), decimalPlaces).toFixed(decimalPlaces))
  }


  /**
   * Write a DXF code & double precision value pair to the list
   */
  writeDoubleValueCode(code, value) {
    let decimalPlaces = 8
    this.writeAttributeCode(code, MathsUtils.roundNumber(parseFloat(value), decimalPlaces).toFixed(decimalPlaces))
  }

  /**
   * Write a DXF code & angle value pair to the list
   */
  writeAngleValueCode(code, value) {
    let decimalPlaces = 8
    this.writeAttributeCode(code, MathsUtils.roundNumber(parseFloat(value), decimalPlaces).toFixed(decimalPlaces))
  }


  /**
   * Write a line entity
   */
  writeLineEntity(startX, startY, endX, endY) {
    this.writeCode(0, 'LINE')
    if(this.currentLayer)
      this.writeAttributeCode(8, this.currentLayer)
    
    this.writePointValueCode(10, startX)
    this.writePointValueCode(20, startY)
    this.writePointValueCode(30, 0)
    this.writePointValueCode(11, endX)
    this.writePointValueCode(21, endY)
    this.writePointValueCode(31, 0)
  }


  /**
   * Write a point entity
   */
   writePointEntity(x, y) {
    this.writeCode(0, 'POINT')
    if(this.currentLayer)
      this.writeAttributeCode(8, this.currentLayer)
    
    this.writePointValueCode(10, x)
    this.writePointValueCode(20, y)
    this.writePointValueCode(30, 0)
  }


  /**
   * Write a circle entity
   */
   writeCircleEntity(x, y, radius) {
    this.writeCode(0, 'CIRCLE')
    if(this.currentLayer)
      this.writeAttributeCode(8, this.currentLayer)
    
    this.writePointValueCode(10, x)
    this.writePointValueCode(20, y)
    this.writePointValueCode(30, 0)
    this.writeDoubleValueCode(40, radius)
  }


  /**
   * Write an arc entity
   */
  writeArcEntity(x, y, radius, startAngle, endAngle) {
    this.writeCode(0, 'ARC')
    if(this.currentLayer)
      this.writeAttributeCode(8, this.currentLayer)
    
    this.writePointValueCode(10, x)
    this.writePointValueCode(20, y)
    this.writePointValueCode(30, 0)
    this.writeDoubleValueCode(40, radius)
    this.writeAngleValueCode(50, startAngle)
    this.writeAngleValueCode(51, endAngle)
  }


  /**
   * Write a polyline entity
   */
  writePolylineEntity(x, y) {
    this.writeCode(0, 'POLYLINE')
    if(this.currentLayer)
      this.writeAttributeCode(8, this.currentLayer)
    
    this.writePointValueCode(10, x)
    this.writePointValueCode(20, y)
    this.writePointValueCode(30, 0)
  }


  /**
   * Write a vertex entity
   */
  writeVertexEntity(x, y, bulge = 0) {
    this.writeCode(0, 'VERTEX')
    if(this.currentLayer)
      this.writeAttributeCode(8, this.currentLayer)
    
    this.writePointValueCode(10, x)
    this.writePointValueCode(20, y)
    this.writePointValueCode(30, 0)
    this.writeDoubleValueCode(42, bulge)
  }


  /**
   * Write an end sequence entity
   */
  writeEndSequenceEntity() {
    this.writeCode(0, 'ENDSEQ')
  }


  /**
   * Write a LWPolyline entity
   * Vertex list should be an array of objects containing x, y, bulge properties
   */
  writeLWPolylineEntity(vertexList) {
    if(vertexList.length == 0)
      return

    this.writeCode(0, 'LWPOLYLINE')
    if(this.currentLayer)
      this.writeAttributeCode(8, this.currentLayer)
    this.writeAttributeCode(90, vertexList.length)
    
    vertexList.forEach(vertex => {
      this.writePointValueCode(10, vertex.x)
      this.writePointValueCode(20, vertex.y)
      this.writeDoubleValueCode(42, vertex.bulge)
    })
  }


  /**
   * Export the DXF to a string
   */
  export() {
    // Close existing section
    if(this.currentSection) {
      this.writeCode(0, 'ENDSEC')
      this.currentSection = null
    }
    this.writeCode(0, 'EOF')
    return this.dxfCodeList.join('\n') + '\n'
  }
  
}